// Cart store — in-memory Zustand store, NOT persisted.
//
// Per the Phase 5 spec the cart resets on reload. The reasoning: a stale cart
// after a refresh can cause double-charges (cashier scans the same items again
// and creates a second transaction), and POS workflows are short and focused
// — losing the cart on tab refresh is the safer default.
//
// Two steps at the till (roadmap batch 4): step one taps products and only
// counts them; step two (/pos/cart) says which lot each unit comes from and
// which promos apply. So a line is one PRODUCT, and its `lots` are how the
// quantity is split across the batches on the event. The split is made for
// the cashier, earliest expiry first (see fillLots); they change it only when
// the box they actually hand over is a different lot.
//
// Pricing math is delegated to @/lib/pricing — the store only holds items and
// fires actions.

import { create } from 'zustand';
import { calculateItemSubtotal, type PricingCartItem, type PromoLine } from '@/lib/pricing';

/** A batch of the product on this event, as offered to the cashier. */
export interface CartLotOption {
  allocationId: string;
  lotNumber?: string;
  expiryDate?: number;
  unit?: string;
  /** Stock left in this batch right now. */
  remaining: number;
}

/** How many units of the line come out of one batch. */
export interface CartLot {
  allocationId: string;
  qty: number;
}

export interface CartItem extends PricingCartItem {
  /** One line per product. */
  lineId: string;
  productId: string;
  name: string;
  price: number;
  qty: number;
  unit?: string;
  /** Total stock across every open batch — the ceiling for qty. */
  maxStock: number;
  /** The batches to choose from, earliest expiry first. */
  lotOptions: CartLotOption[];
  /** The split. Complete when the quantities add up to `qty`. */
  lots: CartLot[];
}

export interface CartTotals {
  itemCount: number;
  subtotal: number;
}

export interface AddItemInput {
  productId: string;
  name: string;
  price: number;
  unit?: string;
  lotOptions: CartLotOption[];
  qty?: number;
  /** A split to restore (draft). Ignored when it does not fit the options. */
  lots?: CartLot[];
}

interface CartState {
  items: CartItem[];
  addItem: (item: AddItemInput) => void;
  removeItem: (lineId: string) => void;
  updateQty: (lineId: string, qty: number) => void;
  /** Replace the lot split of a line. Quantities are clamped to the batch. */
  setLots: (lineId: string, lots: CartLot[]) => void;
  /** Throw away the cashier's split and take units by earliest expiry again. */
  autoLots: (lineId: string) => void;
  /** Refresh lot options from live stock (allocations changed). */
  refreshLots: (byProduct: Map<string, CartLotOption[]>) => void;
  clear: () => void;
  getSubtotal: () => number;
  getItemCount: () => number;
}

const clampQty = (qty: number, max: number): number => {
  if (!Number.isFinite(qty)) return 0;
  if (qty < 0) return 0;
  if (qty > max) return max;
  return Math.floor(qty);
};

const totalStock = (opts: CartLotOption[]) => opts.reduce((s, o) => s + Math.max(0, o.remaining), 0);

const isExpired = (o: CartLotOption) => !!o.expiryDate && o.expiryDate < Date.now();
/** Earliest expiry first; undated lots last. */
const byExpiry = (options: CartLotOption[]) =>
  [...options].sort((a, b) => (a.expiryDate ?? Infinity) - (b.expiryDate ?? Infinity));

/**
 * The split for a quantity — first-expiry-first-out by default.
 *
 * The cashier no longer picks lots by hand for every sale: units come out of
 * the lot that expires soonest, then the next. What the cashier has already
 * set is kept (they may have handed over a specific box), and only the
 * difference is filled or trimmed:
 *   - more units: the lot they chose takes them if it has room, otherwise the
 *     earliest-expiring lots with stock left;
 *   - fewer units: taken back from the latest-expiring lot first.
 * Expired lots are never filled automatically — selling one stays a deliberate
 * choice. A single lot is used as is, as before.
 */
function fillLots(qty: number, options: CartLotOption[], current: CartLot[]): CartLot[] {
  if (qty <= 0) return [];
  if (options.length === 1) return [{ allocationId: options[0].allocationId, qty: Math.min(qty, options[0].remaining) }];

  const byId = new Map(options.map((o) => [o.allocationId, o]));
  const lots = current
    .filter((l) => byId.has(l.allocationId) && l.qty > 0)
    .map((l) => ({ allocationId: l.allocationId, qty: Math.min(l.qty, byId.get(l.allocationId)!.remaining) }))
    .filter((l) => l.qty > 0);
  const sum = lots.reduce((s, l) => s + l.qty, 0);
  if (sum === qty) return lots;

  if (sum > qty) {
    let over = sum - qty;
    for (const o of byExpiry(options).reverse()) {
      const l = lots.find((x) => x.allocationId === o.allocationId);
      if (!l || over <= 0) continue;
      const cut = Math.min(over, l.qty);
      l.qty -= cut;
      over -= cut;
    }
    return lots.filter((l) => l.qty > 0);
  }

  let need = qty - sum;
  // One lot chosen and it can carry the extra: follow the stepper.
  if (lots.length === 1) {
    const room = byId.get(lots[0].allocationId)!.remaining - lots[0].qty;
    if (room >= need) return [{ allocationId: lots[0].allocationId, qty: qty }];
  }
  for (const o of byExpiry(options)) {
    if (need <= 0) break;
    if (isExpired(o)) continue;
    const l = lots.find((x) => x.allocationId === o.allocationId);
    const room = o.remaining - (l?.qty ?? 0);
    if (room <= 0) continue;
    const take = Math.min(room, need);
    if (l) l.qty += take;
    else lots.push({ allocationId: o.allocationId, qty: take });
    need -= take;
  }
  return lots;
}

export const useCart = create<CartState>((set, get) => ({
  items: [],

  addItem: (item) => {
    const options = item.lotOptions.filter((o) => o.remaining > 0);
    const safeMax = totalStock(options);
    if (safeMax <= 0) return; // cannot add out-of-stock items
    const lineId = item.productId;

    set((state) => {
      const existing = state.items.find((i) => i.lineId === lineId);
      if (existing) {
        const nextQty = clampQty(existing.qty + (item.qty ?? 1), safeMax);
        if (nextQty === existing.qty) return state;
        return {
          items: state.items.map((i) =>
            i.lineId === lineId
              ? { ...i, qty: nextQty, lotOptions: options, maxStock: safeMax,
                  lots: fillLots(nextQty, options, i.lots) }
              : i,
          ),
        };
      }
      const nextQty = clampQty(item.qty ?? 1, safeMax);
      const newItem: CartItem = {
        lineId,
        productId: item.productId,
        name: item.name,
        price: item.price,
        unit: item.unit,
        qty: nextQty,
        maxStock: safeMax,
        lotOptions: options,
        lots: fillLots(nextQty, options, item.lots ?? []),
      };
      return { items: [...state.items, newItem] };
    });
  },

  removeItem: (lineId) => {
    set((state) => ({ items: state.items.filter((i) => i.lineId !== lineId) }));
  },

  updateQty: (lineId, qty) => {
    set((state) => {
      const existing = state.items.find((i) => i.lineId === lineId);
      if (!existing) return state;
      const nextQty = clampQty(qty, existing.maxStock);
      if (nextQty <= 0) return { items: state.items.filter((i) => i.lineId !== lineId) };
      if (nextQty === existing.qty) return state;
      return {
        items: state.items.map((i) =>
          i.lineId === lineId ? { ...i, qty: nextQty, lots: fillLots(nextQty, i.lotOptions, i.lots) } : i,
        ),
      };
    });
  },

  setLots: (lineId, lots) => {
    set((state) => ({
      items: state.items.map((i) => {
        if (i.lineId !== lineId) return i;
        const byId = new Map(i.lotOptions.map((o) => [o.allocationId, o]));
        const clean = lots
          .filter((l) => byId.has(l.allocationId))
          .map((l) => ({ allocationId: l.allocationId, qty: clampQty(l.qty, byId.get(l.allocationId)!.remaining) }))
          .filter((l) => l.qty > 0);
        return { ...i, lots: clean };
      }),
    }));
  },

  autoLots: (lineId) => {
    set((state) => ({
      items: state.items.map((i) => (i.lineId === lineId ? { ...i, lots: fillLots(i.qty, i.lotOptions, []) } : i)),
    }));
  },

  refreshLots: (byProduct) => {
    set((state) => ({
      items: state.items.map((i) => {
        const options = (byProduct.get(i.productId) ?? i.lotOptions).filter((o) => o.remaining > 0);
        const max = totalStock(options);
        const qty = clampQty(i.qty, max);
        return { ...i, lotOptions: options, maxStock: max, qty, lots: fillLots(qty, options, i.lots) };
      }).filter((i) => i.qty > 0),
    }));
  },

  clear: () => set({ items: [] }),

  getSubtotal: () => calculateItemSubtotal(get().items),
  getItemCount: () => get().items.reduce((sum, i) => sum + i.qty, 0),
}));

// ==== Derived helpers ====

/** Units assigned to batches so far. */
export const assignedQty = (item: CartItem): number => item.lots.reduce((s, l) => s + l.qty, 0);
/** A line can be charged only when every unit has a batch. */
export const isAssigned = (item: CartItem): boolean => assignedQty(item) === item.qty;

/**
 * The lines create_transaction() will see: one per (product, batch), in cart
 * order. Both the preview and the payload are built from this, so the promo
 * arithmetic on screen is the arithmetic on the invoice.
 */
export function expandLines(items: ReadonlyArray<CartItem>): PromoLine[] {
  const out: PromoLine[] = [];
  for (const i of items) {
    for (const l of i.lots) {
      if (l.qty <= 0) continue;
      out.push({ key: l.allocationId, productId: i.productId, price: i.price, qty: l.qty });
    }
  }
  return out;
}

// ==== Stable selectors (avoid spurious re-renders) ====

export const selectCartItems = (s: CartState): CartItem[] => s.items;
export const selectCartItemCount = (s: CartState): number =>
  s.items.reduce((sum, i) => sum + i.qty, 0);
export const selectCartSubtotal = (s: CartState): number =>
  calculateItemSubtotal(s.items);
export const selectCartTotals = (s: CartState): CartTotals => ({
  itemCount: s.items.reduce((sum, i) => sum + i.qty, 0),
  subtotal: calculateItemSubtotal(s.items),
});

/**
 * Pure helper: read the cart contents without subscribing.
 * Use in callbacks / event handlers where you need a snapshot.
 */
export const readCart = (): CartItem[] => useCart.getState().items;
